import { CostIds } from '@constants'
import { EMkBookStepName, ICost, TMkBookStep, TMkStep } from '@book/types'

export const POINTS_PROTECTION_FEE_IDS = [
  CostIds.PROTECTION_FEE,
  'protection',
  'points-protection-fee',
  'points-protection',
] as const

export const isPointsProtectionCharge = (cost: ICost): boolean => {
  const id = (cost.id || '').toLowerCase()
  const label = (cost.label || '').toLowerCase()

  if (POINTS_PROTECTION_FEE_IDS.some((feeId) => feeId === id)) {
    return true
  }

  return id.includes('protection') || label.includes('protection')
}

export const findPointsProtectionCharge = (costs: ICost[] = []): ICost | undefined => {
  for (const cost of costs) {
    if (isPointsProtectionCharge(cost)) {
      return cost
    }

    if (cost.itemized?.length) {
      const nested = findPointsProtectionCharge(cost.itemized)
      if (nested) {
        return nested
      }
    }
  }

  return undefined
}

export const findPointsProtectionFeeAmount = ({
  paymentCharges = [],
  getCostById,
  completeBookingStep,
}: {
  paymentCharges?: ICost[]
  getCostById?: (costId: string) => ICost | undefined
  completeBookingStep?: TMkStep | TMkBookStep
}): number => {
  if (getCostById) {
    for (const id of POINTS_PROTECTION_FEE_IDS) {
      const amount = getCostById(id)?.amount
      if (amount != null && amount > 0) {
        return amount
      }
    }
  }

  const protectionCharge = findPointsProtectionCharge(paymentCharges)
  if (protectionCharge?.amount != null && protectionCharge.amount > 0) {
    return protectionCharge.amount
  }

  const incursAmount = (completeBookingStep as TMkStep | undefined)?.fieldSet?.protection?.incurs?.amount
  return incursAmount != null && incursAmount > 0 ? incursAmount : 0
}

export const shouldHideSirPointsProtectionPaymentCharges = ({
  themeBrand,
  activeStep,
}: {
  themeBrand?: string
  activeStep?: TMkStep
}): boolean => {
  if ((themeBrand || '').toLowerCase() !== 'sir') {
    return false
  }

  if (activeStep?.name !== EMkBookStepName.COMPLETE_BOOKING || !activeStep.conditionals?.protection) {
    return false
  }

  return activeStep.formState?.protection !== 'yes'
}

export const adjustTotalPaymentDueForSirDeselectedProtection = ({
  themeBrand,
  activeStep,
  paymentCharges,
  totalPaymentDue,
  getCostById,
}: {
  themeBrand?: string
  activeStep?: TMkStep
  paymentCharges?: ICost[]
  totalPaymentDue?: number
  getCostById?: (costId: string) => ICost | undefined
}): number => {
  const baseTotal = totalPaymentDue || 0

  if (!shouldHideSirPointsProtectionPaymentCharges({ themeBrand, activeStep })) {
    return baseTotal
  }

  const protectionFeeAmount = findPointsProtectionFeeAmount({
    paymentCharges,
    getCostById,
    completeBookingStep: activeStep,
  })

  return Math.max(0, baseTotal - protectionFeeAmount)
}

export const updatePointsProtectionCostsShowInSummary = (costs: ICost[], showInSummary: boolean): ICost[] => {
  let changed = false

  const updated = costs.map((cost) => {
    let nextCost = cost

    if (isPointsProtectionCharge(cost) && cost.showInSummary !== showInSummary) {
      changed = true
      nextCost = { ...cost, showInSummary }
    }

    if (cost.itemized?.length) {
      const updatedItemized = updatePointsProtectionCostsShowInSummary(cost.itemized, showInSummary)
      if (updatedItemized !== cost.itemized) {
        changed = true
        nextCost = { ...nextCost, itemized: updatedItemized }
      }
    }

    return nextCost
  })

  return changed ? updated : costs
}
